import useAuthenticatedClients from '../../../hooks/useAuthenticatedClients';
import { queryKeys } from '../../../constants';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { EmployeeDto } from '../../../api';
import { GridRowId } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { onMutationError } from '../../../utils/Utils';

function EmployeeQueries() {
  const { getEmployeesClient } = useAuthenticatedClients();
  const { EMPLOYEES_QUERY_KEY, IS_MANAGER_QUERY_KEY } = queryKeys;
  const queryClient = useQueryClient();

  const IsManager = () => {
    const result = useQuery(IS_MANAGER_QUERY_KEY, async () => {
      return getEmployeesClient().then(client => client.isManager());
    });
    return result;
  };

  /**
   * Updates the employees when a new employee is created
   */
  const { mutate: mutateCreateEmployee } = useMutation<void, Error, EmployeeDto>(createEmployee, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(EMPLOYEES_QUERY_KEY);
      await queryClient.invalidateQueries(IS_MANAGER_QUERY_KEY);
      toast.success('Successfully created employee');
    },
    onError: onMutationError,
  });

  /**
   * Updates the employees when an existing employee is updated
   */
  const { mutate: mutateUpdateEmployee } = useMutation<void, Error, EmployeeDto>(updateEmployee, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(EMPLOYEES_QUERY_KEY);
      await queryClient.invalidateQueries(IS_MANAGER_QUERY_KEY);
      toast.success('Successfully updated employee');
    },
    onError: onMutationError,
  });

  /**
   * Updates the employees when an employee is deleted
   */
  const { mutate: mutateDeleteEmployee } = useMutation<void, Error, GridRowId>(deleteEmployee, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(EMPLOYEES_QUERY_KEY);
      await queryClient.invalidateQueries(IS_MANAGER_QUERY_KEY);
      toast.success('Successfully deleted employee');
    },
    onError: onMutationError,
  });

  /**
   * Creates a POST request to create a new employee
   * @param employeeDto Employee DTO containing data to create a new Employee
   */
  async function createEmployee(employeeDto: EmployeeDto) {
    const client = await getEmployeesClient();
    await client.createEmployee(employeeDto);
  }

  /**
   * Creates a PUT request to update an existing employee
   * @param employeeDto Employee DTO containing data to update an existing Employee
   */
  async function updateEmployee(employeeDto: EmployeeDto) {
    const client = await getEmployeesClient();
    await client.updateEmployee(employeeDto);
  }

  /**
   * Creates a DELETE request to delete an existing employee
   * @param id the id of the employee to delete
   */
  async function deleteEmployee(id: GridRowId) {
    const client = await getEmployeesClient();
    await client.deleteEmployee(id as number);
  }

  return {
    mutateCreateEmployee,
    mutateUpdateEmployee,
    mutateDeleteEmployee,
    IsManager,
  };
}

export default EmployeeQueries;
